import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authService } from '../services/api';

// Criando o contexto
const AuthContext = createContext();

// Hook personalizado para usar o contexto
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  return context;
};

// Provider do contexto
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const checkAuthStatus = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const response = await authService.getMe();
        if (response.success) {
          setUser(response.user);
          setIsAuthenticated(true);
        } else {
          localStorage.removeItem('token');
        }
      }
    } catch (error) {
      console.error('Erro ao verificar status de autenticação:', error);
      localStorage.removeItem('token');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuthStatus();
  }, [checkAuthStatus]);

  const login = async (email, senha) => {
    try {
      const response = await authService.login(email, senha);
      
      if (response.success) {
        localStorage.setItem('token', response.token);
        setUser(response.user);
        setIsAuthenticated(true);
        return { success: true };
      } else {
        return { success: false, message: response.message };
      }
    } catch (error) {
      console.error('Erro no login:', error);
      return { 
        success: false, 
        message: error.response?.data?.message || 'Erro ao fazer login' 
      };
    }
  };

  const logout = async () => {
    try {
      // Tentar fazer logout no servidor
      if (isAuthenticated) {
        await authService.logout();
      }
    } catch (error) {
      console.error('Erro ao fazer logout no servidor:', error);
    } finally {
      // Limpar dados locais
      localStorage.removeItem('token');
      authService.removeAuthToken();
      setUser(null);
      setIsAuthenticated(false);
    }
  };

  const updateUser = (updatedUser) => {
    setUser(prevUser => ({ ...prevUser, ...updatedUser }));
  };

  const hasPermission = (requiredPermission) => {
    if (!user) return false;
    
    const permissions = {
      'admin': ['admin'],
      'editor': ['admin', 'editor'],
      'viewer': ['admin', 'editor', 'viewer', 'auxiliar', 'reuniao_criancas']
    };
    
    return permissions[requiredPermission]?.includes(user.permissao);
  };

  // Verifica permissão granular por módulo
  // action: 'read' | 'write'
  const hasModulePermission = (modulo, action = 'read') => {
    if (!user) return false;
    // admin sempre tem tudo
    if (user.permissao === 'admin') return true;
    const modulos = user.permissoes_modulos || user.permissoesModulos;
    if (!modulos || typeof modulos !== 'object') {
      // fallback: usar role-based
      if (action === 'read') return hasPermission('viewer');
      if (action === 'write') return hasPermission('editor');
      return false;
    }
    const mod = modulos[modulo];
    if (!mod) return false;
    return !!mod[action];
  };

  const canWriteModule = (modulo) => hasModulePermission(modulo, 'write');
  const canReadModule = (modulo) => hasModulePermission(modulo, 'read');

  const isAdmin = () => hasPermission('admin');
  const isEditor = () => hasPermission('editor');
  const canView = () => hasPermission('viewer');

  const contextValue = {
    // Estado
    user,
    loading,
    isAuthenticated,
    
    // Ações
    login,
    logout,
    updateUser,
    checkAuthStatus,
    
    // Verificações de permissão
    hasPermission,
    hasModulePermission,
    canWriteModule,
    canReadModule,
    isAdmin,
    isEditor,
    canView
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}; 